import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";

const seasons = [
    { name: "Air", images: ["airplane.png", "helicopter.png", "hotairballoon.png", "rocket.png"] },
    { name: "Water", images: ["boat.png", "ship.png", "submarine.png", "yacht.png"] },
    { name: "Land", images: ["car.png", "bus.png", "bicycle.png", "truck.png", "taxi.png", "train.png", "ambulance.png"] }
];

const getRandomUniqueOptions = (correctOption: { name: any; images?: string[]; }) => {
    const options = [correctOption];
    const availableOptions = seasons.filter(season => season.name !== correctOption.name);
    while (options.length < 3) {
        const randomIndex = Math.floor(Math.random() * availableOptions.length);
        options.push(availableOptions[randomIndex]);
        availableOptions.splice(randomIndex, 1);
    }
    return options.sort(() => Math.random() - 0.5);
};

const GuessTransport = () => {
    const getNewQuestion = () => {

        document.title = "Guess the Transport – Nursery Worksheet | Mama’s Guidance"; // Set page title
        const newQuestion = seasons[Math.floor(Math.random() * seasons.length)];
        const randomImage = newQuestion.images[Math.floor(Math.random() * newQuestion.images.length)];
        return { newQuestion, newImage: randomImage, newOptions: getRandomUniqueOptions(newQuestion) };
    };

    const [{ newQuestion: question, newImage: image, newOptions: options }, setQuizState] = useState(getNewQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const handleAnswer = (selected: string) => {
        if (selected === question.name) {
            setShowImage(true);
            setTimeout(() => {
                setQuizState(getNewQuestion());
                setFeedback("");
                setShowImage(false);
            }, 3000);
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    const nextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {

            setQuizState(getNewQuestion());
            setFeedback("");
            setShowImage(false);

        }, 5000)
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Introduce nursery kids to various modes of transport with the "Guess the Transport" activity on Mamas Guidance—an interactive game that helps children identify different modes of transport through fun clues and visuals, promoting awareness of travel methods.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord matchImage match-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Guess the Transport</h1>

                                    <p>
                                        Vroom! Zoom! Let’s learn about vehicles in a fun and exciting way! With our <strong>Guess the Transport</strong> worksheet, nursery kids will <strong>identify different modes of transport</strong> — land, air, and water — based on fun picture clues or short descriptions.
                                    </p>

                                    <h2>🚗 What’s in the Worksheet?</h2>
                                    <p>
                                        Children are shown images or clues like <strong>“I fly in the sky”, “I sail on water”, or “I have four wheels and run on roads”</strong>. They must <strong>guess the correct type of vehicle</strong> — such as <b><em>car, bus, airplane, train, boat, bicycle</em></b> — from a set of given options.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify various modes of transport (<strong>land, air, water</strong>)</li>
                                        <li>Understand where and how each vehicle is used</li>
                                        <li>Enhance vocabulary and observation skills</li>
                                        <li>Develop logical reasoning through clue-based guessing</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Read the question or show the picture to your child.</li>
                                        <li>Discuss the clue and help them think of the transport type.</li>
                                        <li>Let them select, circle, or say the correct answer.</li>
                                        <li>Extend learning by matching toy vehicles or using a real-world outing example.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess the Area</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Pick Color</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess Community Helper</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why It’s Important</h2>
                                    <p>
                                        Learning about transport introduces children to the broader world around them — from vehicles they see daily to new ones they may fly in or see on TV. It encourages <strong>curiosity, spatial understanding, and early classification skills</strong>.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Guess the Transport</h1>

                                <p>
                                    Vroom! Zoom! Let’s learn about vehicles in a fun and exciting way! With our <strong>Guess the Transport</strong> worksheet, nursery kids will <strong>identify different modes of transport</strong> — land, air, and water — based on fun picture clues or short descriptions.
                                </p>

                                <h2>🚗 What’s in the Worksheet?</h2>
                                <p>
                                    Children are shown images or clues like “I fly in the sky”, “I sail on water”, or “I have four wheels and run on roads”. They must <strong>guess the correct type of vehicle</strong> — such as <em>car, bus, airplane, train, boat, bicycle</em> — from a set of given options.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify various modes of transport (land, air, water)</li>
                                    <li>Understand where and how each vehicle is used</li>
                                    <li>Enhance vocabulary and observation skills</li>
                                    <li>Develop logical reasoning through clue-based guessing</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Read the question or show the picture to your child.</li>
                                    <li>Discuss the clue and help them think of the transport type.</li>
                                    <li>Let them select, circle, or say the correct answer.</li>
                                    <li>Extend learning by matching toy vehicles or using a real-world outing example.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess the Area</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Pick Color</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess Community Helper</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why It’s Important</h2>
                                <p>
                                    Learning about transport introduces children to the broader world around them — from vehicles they see daily to new ones they may fly in or see on TV. It encourages curiosity, spatial understanding, and early classification skills.
                                </p>
                            </article>
                            <div className="mt-4">
                                <img src={process.env.REACT_APP_API_URL + image} className="season-image helper-image" />
                            </div>
                            <div className="mt-4" style={{ display: 'ruby' }}>
                                {options.map((option, index) => (
                                    <div key={index} style={{ margin: '10px' }}>
                                        <button className="optionButton background-blue" onClick={() => handleAnswer(option.name)}>{option.name}</button>
                                    </div>
                                ))}
                            </div>
                            <h3>{feedback}</h3>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default GuessTransport;
